import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spinner } from './ui/Spinner';
import { EmptyState } from './ui/EmptyState';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { matchesSearch, parseSearchQuery, rowSearchValues } from '../lib/tableSearch';
import { fmt } from '../lib/formatters';
import { AgruparMenu, ColumnasMenu, VistasMenu, useVistas, type EstadoVista, type Vista } from './table/TablaMenus';

/** Filas visibles partidas por el valor de la columna de agrupación, en orden de aparición. */
function agrupar<T extends Record<string, unknown>>(rows: T[], col: Column<T>): { clave: string; etiqueta: string; rows: T[] }[] {
  const grupos = new Map<string, { clave: string; etiqueta: string; rows: T[] }>();
  for (const r of rows) {
    const v = col.sortValue ? col.sortValue(r) : (r[col.key] as unknown);
    const etiqueta = v == null || v === '' ? '—' : String(v);
    let g = grupos.get(etiqueta);
    if (!g) { g = { clave: etiqueta, etiqueta, rows: [] }; grupos.set(etiqueta, g); }
    g.rows.push(r);
  }
  return [...grupos.values()].sort((a, b) => (a.etiqueta === '—' ? 1 : b.etiqueta === '—' ? -1 : a.etiqueta.localeCompare(b.etiqueta, 'es')));
}

/** Total de una columna sobre las filas visibles. */
function totalDeColumna<T extends Record<string, unknown>>(c: Column<T>, rows: T[]): ReactNode {
  if (!c.total) return null;
  if (typeof c.total === 'function') return c.total(rows);
  if (c.total === 'count') return fmt.int(rows.length);
  const valores = rows
    .map((r) => (c.totalValue ? c.totalValue(r) : c.sortValue ? c.sortValue(r) : (r[c.key] as unknown)))
    .map((v) => (typeof v === 'number' ? v : v == null || v === '' ? NaN : Number(v)))
    .filter((n) => Number.isFinite(n));
  if (valores.length === 0) return '—';
  const suma = valores.reduce((a, b) => a + b, 0);
  const n = c.total === 'avg' ? suma / valores.length : suma;
  return c.totalFormat ? c.totalFormat(n) : fmt.number(n);
}

export type Column<T> = {
  key: string;
  label: string;
  /** Devuelve un nodo renderizable. Por defecto, accede a `row[key]`. */
  render?: (row: T) => ReactNode;
  /** Función para extraer el valor de orden si quieres ordenar por algo distinto al render. */
  sortValue?: (row: T) => string | number | null | undefined;
  /**
   * Texto contra el que busca el buscador de la tabla. Necesario en columnas
   * cuya `key` es sintética (no existe como propiedad de la fila) o que
   * renderizan varios campos a la vez: sin esto el buscador no las ve.
   * Por defecto: `row[key]` y, si la key es sintética, `sortValue(row)`.
   */
  searchValue?: (row: T) => string | number | null | undefined;
  /** Si false, la columna no es ordenable. */
  sortable?: boolean;
  className?: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /**
   * Total al pie de la tabla sobre las filas visibles (tras búsqueda y
   * filtros): 'sum' | 'avg' | 'count', o una función que recibe las filas y
   * devuelve el nodo. Con 'sum'/'avg' el número sale de `totalValue(row)` o,
   * si no se da, de `sortValue(row)` / `row[key]`. Se formatea con
   * `totalFormat` (por defecto, número con separador de miles).
   */
  total?: 'sum' | 'avg' | 'count' | ((rows: T[]) => ReactNode);
  totalValue?: (row: T) => number | null | undefined;
  totalFormat?: (n: number) => ReactNode;
  /** La columna puede usarse en «Agrupar por» (cabeceras de grupo con subtotales). */
  groupable?: boolean;
  /** Oculta por defecto; el usuario la activa en «Columnas» (se recuerda). */
  defaultHidden?: boolean;
  /** Grupo de cabecera: las columnas seguidas con el mismo grupo comparten una fila de título encima. */
  headerGroup?: string;
  /** Color suave del grupo de cabecera. Por defecto, uno de la paleta según el orden del grupo. */
  headerGroupColor?: ColorGrupo;
};

export type ColorGrupo = 'slate' | 'sky' | 'amber' | 'emerald' | 'violet' | 'rose';

/** Clases literales (Tailwind no ve clases construidas): título del grupo y cabeceras de sus columnas. */
const COLORES_GRUPO: Record<ColorGrupo, { titulo: string; columna: string }> = {
  slate: { titulo: 'bg-slate-200/70 text-slate-700 border-slate-300', columna: 'bg-slate-100/70 hover:bg-slate-200/60' },
  sky: { titulo: 'bg-sky-100 text-sky-800 border-sky-200', columna: 'bg-sky-50 hover:bg-sky-100' },
  amber: { titulo: 'bg-amber-100 text-amber-800 border-amber-200', columna: 'bg-amber-50 hover:bg-amber-100' },
  emerald: { titulo: 'bg-emerald-100 text-emerald-800 border-emerald-200', columna: 'bg-emerald-50 hover:bg-emerald-100' },
  violet: { titulo: 'bg-violet-100 text-violet-800 border-violet-200', columna: 'bg-violet-50 hover:bg-violet-100' },
  rose: { titulo: 'bg-rose-100 text-rose-800 border-rose-200', columna: 'bg-rose-50 hover:bg-rose-100' },
};
const PALETA_GRUPOS: ColorGrupo[] = ['sky', 'amber', 'emerald', 'violet', 'rose'];

/** Color de cada grupo de cabecera: el que fije la columna o el siguiente de la paleta. */
function coloresDeGrupos<T>(cols: Column<T>[]): Map<string, ColorGrupo> {
  const out = new Map<string, ColorGrupo>();
  for (const c of cols) {
    if (!c.headerGroup || out.has(c.headerGroup)) continue;
    out.set(c.headerGroup, c.headerGroupColor ?? PALETA_GRUPOS[out.size % PALETA_GRUPOS.length]);
  }
  return out;
}

/** Tramos seguidos de columnas con el mismo `headerGroup` (los sin grupo, en blanco). */
function gruposCabecera<T>(cols: Column<T>[]): { label: string | undefined; span: number }[] {
  const out: { label: string | undefined; span: number }[] = [];
  for (const c of cols) {
    const ultimo = out[out.length - 1];
    if (ultimo && ultimo.label === c.headerGroup) ultimo.span += 1;
    else out.push({ label: c.headerGroup, span: 1 });
  }
  return out;
}

export type NumberCompareOp = '>' | '<' | '>=' | '<=' | '=';

/**
 * Parsea el valor codificado de un filtro numberCompare (p. ej. ">20", "<=5").
 * Devuelve null si el valor no representa una comparación completa.
 */
function parseNumberCompare(v: string): { op: NumberCompareOp; n: number } | null {
  const m = v.match(/^(>=|<=|>|<|=)\s*(-?\d+(?:[.,]\d+)?)$/);
  if (!m) return null;
  const n = Number(m[2].replace(',', '.'));
  if (!Number.isFinite(n)) return null;
  return { op: m[1] as NumberCompareOp, n };
}

export type DataTableFilter<T = Record<string, unknown>> = {
  key: string;
  label: string;
  type?: 'select' | 'numberCompare' | 'boolean' | 'booleanParam' | 'text' | 'numberRange';
  options?: { value: string; label: string }[];
  /** Solo 'select': filas con estos valores que no salen nunca salvo que el filtro los elija expresamente (ni con «Todos»). */
  hiddenUnlessSelected?: string[];
  /** Valor inicial del filtro al montar la tabla. Para boolean usar 'true' o ''. */
  defaultValue?: string;
  /** Placeholder para los tipos 'text' y 'numberRange'. */
  placeholder?: string;
  /**
   * Para numberCompare / numberRange: cómo extraer el número de la fila.
   * Para boolean: devuelve true si la fila debe pasar cuando el check está marcado.
   * Para text: devuelve el string contra el que se busca (case-insensitive, includes).
   */
  getValue?: (row: T) => number | boolean | string | null | undefined;
  /**
   * Solo para 'booleanParam': predicado parametrizado por el umbral que el
   * usuario teclea junto al check. Se evalúa con el número ya parseado.
   */
  getValueParam?: (row: T, param: number) => boolean;
  /** Solo para 'booleanParam': umbral con el que se activa el filtro al marcarlo. */
  paramDefault?: number;
  /** Solo para 'booleanParam': unidad que se pinta tras el input (p. ej. 'meses'). */
  paramSuffix?: string;
  /**
   * Para 'boolean' y 'booleanParam': al ACTIVARLO, la tabla se ordena por esta
   * columna. Útil en filtros de alerta, donde lo urgente debe quedar arriba sin
   * que el usuario tenga que ordenar a mano. Al desactivar no se restaura el
   * orden anterior: se deja el que haya, que es lo menos sorprendente. En
   * booleanParam solo actúa al activar, no al reteclear el umbral.
   */
  sortWhenActive?: { key: string; desc?: boolean };
};

export type DataTableProps<T> = {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  empty?: ReactNode;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  filters?: DataTableFilter<T>[];
  searchPlaceholder?: string;
  defaultSort?: { key: string; desc?: boolean };
  /** Header sticky dentro del propio contenedor scrollable. Default true:
   *  el listado tiene scroll vertical + horizontal interno y la cabecera
   *  se queda fija al hacer scroll. Para desactivar (raro), pasa false. */
  stickyHeader?: boolean;
  /** Altura máxima del cuerpo. Default `calc(100vh - 240px)` — ajustable
   *  por listado si hay paneles arriba ocupando alto. */
  maxBodyHeight?: string;
  /** Si true, muestra una columna de checkboxes al principio para selección múltiple. */
  selectable?: boolean;
  /** Set controlado de ids seleccionados. Necesario si selectable=true. */
  selectedIds?: Set<string>;
  /** Callback cuando cambia la selección. Recibe el nuevo Set. */
  onSelectionChange?: (next: Set<string>) => void;
  /** Nodo extra que se pinta junto a los filtros (útil para etiquetas
   *  informativas como "Stock actualizado: dd-mm-yyyy"). */
  filterExtras?: ReactNode;
  /** Si false, oculta el buscador interno (útil cuando la página ya tiene
   *  su propio buscador server-side y no queremos duplicarlo). Default true. */
  searchable?: boolean;
  /** Si se pasa, el estado (query, filtros, orden) se sincroniza con la
   *  URL bajo prefijo `${storageKey}_` para que el back-button del navegador
   *  restaure el listado tal cual. Pasar uno distinto a cada DataTable cuando
   *  hay más de una en la misma URL (p. ej. tabs). Default: prefijo vacío
   *  (suficiente para páginas con una sola tabla). */
  storageKey?: string;
  /** Notifica las filas tras aplicar búsqueda, filtros y orden — exactamente
   *  las que se ven en la tabla. Útil para exportar la vista actual. */
  onFilteredRowsChange?: (rows: T[]) => void;
  /** Si false, el estado no se lee ni se escribe en la URL (tablas embebidas
   *  en una ficha: varias por página y la URL no debe cambiar al entrar). */
  syncUrl?: boolean;
  /** Doble clic en una fila (p. ej. abrir la ficha cuando el clic simple abre el panel lateral). */
  onRowDoubleClick?: (row: T) => void;
  /** Si se pasa, en vez de la tabla se pinta esto con las filas ya filtradas y
   *  ordenadas (p. ej. una vista de catálogo en tarjetas). Buscador, filtros y
   *  vistas guardadas siguen funcionando; columnas y agrupar no aplican. */
  renderBody?: (rows: T[]) => ReactNode;
  /** Panel a la derecha de la tabla (a partir de xl), alineado con su borde
   *  superior y fijo al hacer scroll. El hueco se reserva aunque esté vacío
   *  para que la tabla no cambie de ancho al abrirlo. */
  aside?: ReactNode;
  /** Filas desplegables: para cada id de expandedIds se pinta debajo de la
   *  fila una fila a todo el ancho con renderExpanded(row). El +/− que abre y
   *  cierra lo pone la página en una de sus columnas. */
  renderExpanded?: (row: T) => ReactNode;
  expandedIds?: Set<string>;
  /** Columna bajo la que empieza lo desplegado (las de su izquierda quedan
   *  vacías) y sangría extra dentro de ella, p. ej. el ancho del +/−. Sigue a
   *  la columna aunque el usuario la mueva; si está oculta, a todo el ancho. */
  expandedAlign?: { key: string; indent?: string };
};

export function DataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  loading,
  empty,
  rowKey,
  onRowClick,
  filters = [],
  searchPlaceholder = 'Buscar…',
  defaultSort,
  stickyHeader = true,
  maxBodyHeight,
  selectable,
  selectedIds,
  onSelectionChange,
  filterExtras,
  searchable = true,
  storageKey,
  onFilteredRowsChange,
  syncUrl = true,
  onRowDoubleClick,
  renderBody,
  aside,
  renderExpanded,
  expandedIds,
  expandedAlign,
}: DataTableProps<T>) {
  const prefix = storageKey ? `${storageKey}_` : '';
  const PK_Q = `${prefix}q`;
  const PK_SK = `${prefix}sk`;
  const PK_SD = `${prefix}sd`;
  const pkFilter = (k: string) => `${prefix}f_${k}`;

  const [searchParamsReal, setSearchParams] = useSearchParams();
  const searchParams = useMemo(() => (syncUrl ? searchParamsReal : new URLSearchParams()), [syncUrl, searchParamsReal]);

  const [sortKey, setSortKey] = useState<string | null>(() => {
    const fromUrl = searchParams.get(PK_SK);
    if (fromUrl !== null) return fromUrl || null;
    return defaultSort?.key ?? null;
  });
  const [sortDesc, setSortDesc] = useState<boolean>(() => {
    const fromUrl = searchParams.get(PK_SD);
    if (fromUrl !== null) return fromUrl === '1';
    return defaultSort?.desc ?? false;
  });
  const [query, setQuery] = useState(() => searchParams.get(PK_Q) ?? '');
  const defaultFilterValues = useMemo<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of filters) {
      if (f.defaultValue) init[f.key] = f.defaultValue;
    }
    return init;
    // Solo se evalúa al montar; cambios posteriores en `filters` no resetean filtros del usuario.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filterValues, setFilterValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = { ...defaultFilterValues };
    for (const f of filters) {
      const fromUrl = searchParams.get(pkFilter(f.key));
      if (fromUrl !== null) init[f.key] = fromUrl;
    }
    return init;
  });

  // ── Columnas ocultas y agrupación ──────────────────────────────────
  // Persisten en localStorage por storageKey (comodidad por navegador) y
  // forman parte de las vistas guardadas (servidor, por usuario).
  const LS_COLS = storageKey ? `pm-approx:cols:${storageKey}` : null;
  const ocultasPorDefecto = () => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.key));
  const [ocultas, setOcultas] = useState<Set<string>>(() => {
    try { if (LS_COLS) { const raw = localStorage.getItem(LS_COLS); if (raw) return new Set(JSON.parse(raw) as string[]); } } catch { /* sin storage */ }
    return ocultasPorDefecto();
  });
  const [grupo, setGrupo] = useState<string | null>(() => { try { return LS_COLS ? localStorage.getItem(`${LS_COLS}:grupo`) : null; } catch { return null; } });
  useEffect(() => {
    try {
      if (!LS_COLS) return;
      localStorage.setItem(LS_COLS, JSON.stringify([...ocultas]));
      if (grupo) localStorage.setItem(`${LS_COLS}:grupo`, grupo); else localStorage.removeItem(`${LS_COLS}:grupo`);
    } catch { /* sin storage */ }
  }, [ocultas, grupo, LS_COLS]);
  const visibleColumns = useMemo(() => columns.filter((c) => !ocultas.has(c.key)), [columns, ocultas]);
  const coloresGrupo = useMemo(() => coloresDeGrupos(columns), [columns]);
  const columnaGrupo = grupo ? columns.find((c) => c.key === grupo && c.groupable) : undefined;

  // ── Vistas guardadas (solo listados completos con storageKey) ──────
  const conVistas = !!storageKey && searchable && syncUrl;
  const { vistas, crear, actualizar, borrar } = useVistas(conVistas ? storageKey : undefined);
  const [vistaActiva, setVistaActiva] = useState<Vista | null>(null);
  const estadoActual: EstadoVista = { q: query, sk: sortKey, sd: sortDesc, filtros: filterValues, ocultas: [...ocultas].sort(), grupo };
  const aplicarVista = (v: Vista) => {
    const e = v.estado ?? ({} as Partial<EstadoVista>);
    setQuery(e.q ?? '');
    setSortKey(e.sk ?? null);
    setSortDesc(!!e.sd);
    setFilterValues({ ...defaultFilterValues, ...(e.filtros ?? {}) });
    setOcultas(new Set(e.ocultas ?? []));
    setGrupo(e.grupo ?? null);
    setVistaActiva(v);
  };
  const resetVista = () => {
    setQuery(''); setSortKey(defaultSort?.key ?? null); setSortDesc(!!defaultSort?.desc);
    setFilterValues(defaultFilterValues); setOcultas(ocultasPorDefecto()); setGrupo(null); setVistaActiva(null);
  };
  // La vista por defecto se aplica al abrir el listado sin estado en la URL (una vez).
  const vistaDefectoAplicada = useRef(false);
  useEffect(() => {
    if (!conVistas || vistaDefectoAplicada.current || vistas.length === 0) return;
    vistaDefectoAplicada.current = true;
    const urlTieneEstado = [...searchParamsReal.keys()].some((k) => k.startsWith(prefix));
    if (urlTieneEstado) return;
    const def = vistas.find((v) => v.por_defecto);
    if (def) aplicarVista(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vistas, conVistas]);
  const normEstado = (e: Partial<EstadoVista>) => JSON.stringify({
    q: e.q ?? '', sk: e.sk ?? null, sd: !!e.sd,
    filtros: Object.fromEntries(Object.entries(e.filtros ?? {}).filter(([, v]) => v !== '').sort()),
    ocultas: [...(e.ocultas ?? [])].sort(), grupo: e.grupo ?? null,
  });
  const estadoEstandar: EstadoVista = { q: '', sk: defaultSort?.key ?? null, sd: !!defaultSort?.desc, filtros: defaultFilterValues, ocultas: [...ocultasPorDefecto()].sort(), grupo: null };
  const vistaModificada = normEstado(estadoActual) !== normEstado(vistaActiva ? vistaActiva.estado : estadoEstandar);

  // Sincroniza el estado con la URL (replace=true para no contaminar el historial
  // con cada tecla pulsada en el buscador). Al volver atrás desde una ficha,
  // la URL anterior se restaura y este componente arranca con los params correctos.
  useEffect(() => {
    if (!syncUrl) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (query) next.set(PK_Q, query);
        else next.delete(PK_Q);
        if (sortKey) next.set(PK_SK, sortKey);
        else next.delete(PK_SK);
        if (sortDesc) next.set(PK_SD, '1');
        else next.delete(PK_SD);
        for (const f of filters) {
          const v = filterValues[f.key] ?? '';
          const def = defaultFilterValues[f.key] ?? '';
          if (v && v !== def) next.set(pkFilter(f.key), v);
          else next.delete(pkFilter(f.key));
        }
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filterValues, sortKey, sortDesc]);

  // Filtros con `sortWhenActive`: al pasar de desmarcado a marcado se fija el
  // orden que declaren. Se compara contra el valor previo para no repisar el
  // orden que el usuario elija después, con el filtro ya activo.
  const filtrosPrev = useRef<Record<string, string>>({});
  useEffect(() => {
    for (const f of filters) {
      if (!f.sortWhenActive) continue;
      const antes = filtrosPrev.current[f.key] ?? '';
      const ahora = filterValues[f.key] ?? '';
      // booleanParam se considera activo con cualquier umbral no vacío, así que
      // la transición es ''→algo: reteclear el umbral no vuelve a fijar el orden.
      const activo = f.type === 'booleanParam' ? ahora !== '' : ahora === 'true';
      const estabaActivo = f.type === 'booleanParam' ? antes !== '' : antes === 'true';
      if (activo && !estabaActivo) {
        setSortKey(f.sortWhenActive.key);
        setSortDesc(!!f.sortWhenActive.desc);
      }
    }
    filtrosPrev.current = { ...filterValues };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues]);

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      if (!sortDesc) setSortDesc(true);
      else {
        setSortKey(null);
        setSortDesc(false);
      }
    } else {
      setSortKey(key);
      setSortDesc(false);
    }
  };

  // Búsqueda + filtros, saltándose opcionalmente uno: las opciones de cada
  // select se calculan con todos los DEMÁS aplicados (filtros en cascada).
  const aplicarFiltros = (base: T[], omitir?: string): T[] => {
    let out = base;
    const raw = query.trim();
    if (raw) {
      const parsed = parseSearchQuery(raw);
      if (parsed.terms.length > 0) {
        out = out.filter((row) => matchesSearch(rowSearchValues(row, columns), parsed));
      }
    }
    for (const f of filters) {
      if (f.key === omitir) continue;
      const v = filterValues[f.key];
      if (f.hiddenUnlessSelected?.length) {
        // Valores que solo salen si el filtro los pide expresamente (también con «Todos»).
        const pedidos = new Set((v ?? '').split('|'));
        const ocultos = f.hiddenUnlessSelected.filter((x) => !pedidos.has(x));
        if (ocultos.length) out = out.filter((row) => !ocultos.includes(String(row[f.key] ?? '')));
      }
      if (!v) continue;
      if (f.type === 'numberCompare') {
        const parsed = parseNumberCompare(v);
        if (!parsed || !f.getValue) continue;
        const { op, n } = parsed;
        out = out.filter((row) => {
          const raw = f.getValue!(row as T);
          if (raw == null || typeof raw !== 'number' || Number.isNaN(raw)) return false;
          switch (op) {
            case '>': return raw > n;
            case '<': return raw < n;
            case '>=': return raw >= n;
            case '<=': return raw <= n;
            case '=': return raw === n;
          }
          return false;
        });
      } else if (f.type === 'boolean') {
        if (v !== 'true' || !f.getValue) continue;
        out = out.filter((row) => Boolean(f.getValue!(row as T)));
      } else if (f.type === 'booleanParam') {
        // Activo = el valor codifica un umbral. Mientras el input esté vacío o
        // a medio teclear el filtro no se aplica, en vez de filtrar con NaN.
        const n = Number(v.replace(',', '.'));
        if (!Number.isFinite(n) || !f.getValueParam) continue;
        out = out.filter((row) => Boolean(f.getValueParam!(row as T, n)));
      } else if (f.type === 'text') {
        const needle = v.toLowerCase().trim();
        if (!needle || !f.getValue) continue;
        out = out.filter((row) => {
          const raw = f.getValue!(row as T);
          return typeof raw === 'string' && raw.toLowerCase().includes(needle);
        });
      } else if (f.type === 'numberRange') {
        // Valor codificado como "min,max"; cada parte puede ir vacía.
        const [minStr, maxStr] = v.split(',');
        const min = minStr ? Number(minStr.replace(',', '.')) : null;
        const max = maxStr ? Number(maxStr.replace(',', '.')) : null;
        if ((min == null || Number.isNaN(min)) && (max == null || Number.isNaN(max))) continue;
        if (!f.getValue) continue;
        out = out.filter((row) => {
          const raw = f.getValue!(row as T);
          if (raw == null || typeof raw !== 'number' || Number.isNaN(raw)) return false;
          if (min != null && !Number.isNaN(min) && raw < min) return false;
          if (max != null && !Number.isNaN(max) && raw > max) return false;
          return true;
        });
      } else {
        // Select normal con soporte multi-valor: "V1|V2" matchea ambos.
        if (v.includes('|')) {
          const allowed = new Set(v.split('|'));
          out = out.filter((row) => allowed.has(String(row[f.key] ?? '')));
        } else {
          out = out.filter((row) => String(row[f.key] ?? '') === v);
        }
      }
    }
    return out;
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    let out = aplicarFiltros(rows);
    if (sortKey) {
      const col = columns.find((c) => c.key === sortKey);
      const getter = col?.sortValue ?? ((r: T) => r[sortKey]);
      out = [...out].sort((a, b) => {
        const av = getter(a as T);
        const bv = getter(b as T);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDesc ? bv - av : av - bv;
        const as = String(av);
        const bs = String(bv);
        return sortDesc ? bs.localeCompare(as, 'es') : as.localeCompare(bs, 'es');
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, filterValues, sortKey, sortDesc, columns, filters]);

  // Opciones de cada select limitadas a lo que queda con el resto de filtros:
  // filtrar Gama=Gaming deja en Subcategoría solo las que hay en Gaming. Se
  // conserva siempre la opción elegida (si no, el select se quedaría en
  // blanco con el filtro aún activo). Opción multi-valor ('A|B') vale si
  // alguno de sus valores aparece. Sin filas (cargando) no se recorta nada.
  const opcionesFiltro = useMemo(() => {
    const res: Record<string, { value: string; label: string }[]> = {};
    if (!rows || rows.length === 0) return res;
    for (const f of filters) {
      if ((f.type ?? 'select') !== 'select' || !f.options) continue;
      const presentes = new Set(aplicarFiltros(rows, f.key).map((row) => String(row[f.key] ?? '')));
      const actual = filterValues[f.key] ?? '';
      res[f.key] = f.options.filter(
        (o) => o.value === actual || o.value.split('|').some((v) => presentes.has(v)),
      );
    }
    return res;
    // aplicarFiltros depende de query/filterValues/columns/filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, filterValues, columns, filters]);

  // Emite las filas visibles SOLO cuando su contenido cambia de verdad. El
  // useMemo de filteredRows produce un array nuevo en cada render (los props
  // `filters`/`columns` suelen recrearse en el padre), así que comparamos por
  // contenido contra lo último emitido: si es igual, no llamamos al callback.
  // Esto evita el bucle infinito setState→render→emit cuando el padre guarda
  // las filas en estado (p. ej. para exportar). onFilteredRowsChange se omite
  // de deps a propósito (suele ser una closure nueva por render).
  const lastEmittedRef = useRef<T[] | null>(null);
  useEffect(() => {
    const prev = lastEmittedRef.current;
    const igual =
      prev != null &&
      prev.length === filteredRows.length &&
      prev.every((r, i) => r === filteredRows[i]);
    if (igual) return;
    lastEmittedRef.current = filteredRows;
    onFilteredRowsChange?.(filteredRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows]);

  return (
    <div>
      {(searchable || filters.length > 0 || !!filterExtras || selectable) && (
      <div className="mb-1 flex flex-wrap items-center gap-2">
        {searchable && (
          <Input
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
        )}
        {filters.map((f) => {
          if (f.type === 'boolean') {
            const checked = (filterValues[f.key] ?? '') === 'true';
            return (
              <label
                key={f.key}
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    setFilterValues((s) => ({ ...s, [f.key]: e.target.checked ? 'true' : '' }))
                  }
                  className="cursor-pointer"
                />
                <span className="whitespace-nowrap">{f.label}</span>
              </label>
            );
          }
          if (f.type === 'booleanParam') {
            // Check + umbral editable en línea. Al marcar entra el valor por
            // defecto y el input aparece enfocable, así el usuario ve con qué
            // número está filtrando y puede cambiarlo sin abrir ningún diálogo.
            const actual = filterValues[f.key] ?? '';
            const checked = actual !== '';
            const porDefecto = String(f.paramDefault ?? 0);
            return (
              <div
                key={f.key}
                className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600"
              >
                <label className="flex cursor-pointer items-center gap-1.5 hover:text-slate-900">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      setFilterValues((s) => ({ ...s, [f.key]: e.target.checked ? porDefecto : '' }))
                    }
                    className="cursor-pointer"
                  />
                  <span className="whitespace-nowrap">{f.label}</span>
                </label>
                {checked && (
                  <>
                    <span className="text-slate-400">&lt;</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step="0.5"
                      value={actual}
                      autoFocus
                      onChange={(e) => setFilterValues((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-slate-700 focus:border-sky-400 focus:outline-none"
                    />
                    {f.paramSuffix && <span className="whitespace-nowrap text-slate-400">{f.paramSuffix}</span>}
                  </>
                )}
              </div>
            );
          }
          if (f.type === 'text') {
            return (
              <label key={f.key} className="flex items-center gap-1 text-xs text-slate-500">
                <span className="whitespace-nowrap">{f.label}:</span>
                <Input
                  value={filterValues[f.key] ?? ''}
                  placeholder={f.placeholder ?? '—'}
                  onChange={(e) => setFilterValues((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="w-44"
                />
              </label>
            );
          }
          if (f.type === 'numberRange') {
            const current = filterValues[f.key] ?? '';
            const [minStr = '', maxStr = ''] = current.split(',');
            const setRange = (nextMin: string, nextMax: string) => {
              const a = nextMin.trim();
              const b = nextMax.trim();
              setFilterValues((s) => ({ ...s, [f.key]: a || b ? `${a},${b}` : '' }));
            };
            return (
              <label key={f.key} className="flex items-center gap-1 text-xs text-slate-500">
                <span className="whitespace-nowrap">{f.label}:</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={minStr}
                  placeholder="min"
                  onChange={(e) => setRange(e.target.value, maxStr)}
                  className="w-20"
                />
                <span className="text-slate-400">–</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={maxStr}
                  placeholder="max"
                  onChange={(e) => setRange(minStr, e.target.value)}
                  className="w-20"
                />
              </label>
            );
          }
          if (f.type === 'numberCompare') {
            const current = filterValues[f.key] ?? '';
            const parsed = parseNumberCompare(current);
            // Si el valor existente no es parseable (p. ej. mid-edición), conservamos lo que el usuario escribió.
            const opMatch = current.match(/^(>=|<=|>|<|=)/);
            const op = (parsed?.op ?? (opMatch?.[1] as NumberCompareOp | undefined) ?? '>') as NumberCompareOp;
            const numStr = current.replace(/^(>=|<=|>|<|=)\s*/, '');
            const setEncoded = (nextOp: NumberCompareOp, nextNum: string) => {
              const cleaned = nextNum.trim();
              setFilterValues((s) => ({ ...s, [f.key]: cleaned ? `${nextOp}${cleaned}` : '' }));
            };
            return (
              <label key={f.key} className="flex items-center gap-1 text-xs text-slate-500">
                <span className="whitespace-nowrap">{f.label}:</span>
                <Select
                  emptyLabel={null}
                  options={[
                    { value: '>', label: '>' },
                    { value: '<', label: '<' },
                    { value: '>=', label: '≥' },
                    { value: '<=', label: '≤' },
                    { value: '=', label: '=' },
                  ]}
                  value={op}
                  onChange={(e) => setEncoded(e.target.value as NumberCompareOp, numStr)}
                  className="w-16"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  value={numStr}
                  placeholder="—"
                  onChange={(e) => setEncoded(op, e.target.value)}
                  className="w-24"
                />
              </label>
            );
          }
          return (
            <label key={f.key} className="flex min-w-[10rem] items-center gap-1 text-xs text-slate-500">
              <span className="whitespace-nowrap">{f.label}:</span>
              <Select
                emptyLabel="Todos"
                options={opcionesFiltro[f.key] ?? f.options ?? []}
                value={filterValues[f.key] ?? ''}
                onChange={(e) =>
                  setFilterValues((s) => ({ ...s, [f.key]: e.target.value }))
                }
              />
            </label>
          );
        })}
        {(() => {
          const filtersDiffer = filters.some(
            (f) => (filterValues[f.key] ?? '') !== (defaultFilterValues[f.key] ?? ''),
          );
          const tieneAlgoQueLimpiar = Boolean(query) || filtersDiffer;
          return (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setFilterValues(defaultFilterValues);
              }}
              disabled={!tieneAlgoQueLimpiar}
              className={`rounded-md border px-2.5 py-1.5 text-xs ${
                tieneAlgoQueLimpiar
                  ? 'cursor-pointer border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300'
              }`}
              title="Quita el texto del buscador y restablece los filtros"
            >
              Limpiar filtros
            </button>
          );
        })()}
        {conVistas && (
          <>
            {!renderBody && <ColumnasMenu columnas={columns.filter((c) => c.label).map((c) => ({ key: c.key, label: c.label }))} ocultas={ocultas} onChange={setOcultas} />}
            {!renderBody && columns.some((c) => c.groupable) && (
              <AgruparMenu opciones={columns.filter((c) => c.groupable).map((c) => ({ key: c.key, label: c.label }))} grupo={grupo} onChange={setGrupo} />
            )}
            <VistasMenu pantalla={storageKey!} vistas={vistas} activa={vistaActiva} modificada={vistaModificada} estadoActual={estadoActual} onAplicar={aplicarVista} onReset={resetVista} crear={crear} actualizar={actualizar} borrar={borrar} />
          </>
        )}
        {filterExtras}
        <span className="ml-auto text-xs text-slate-500">
          {loading ? '…' : `${filteredRows.length} fila${filteredRows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      )}
      {searchable && (
        <p className="mb-2 text-[10px] leading-tight text-slate-400">
          Varias palabras = todas deben coincidir · <span className="font-medium">palabra1 / palabra2</span> = cualquiera coincide · <span className="font-medium">"frase exacta"</span> = coincidencia literal
        </p>
      )}

      <div className={aside ? 'grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]' : 'contents'}>
      <div className={aside ? 'min-w-0' : 'contents'}>
      {renderBody ? renderBody(filteredRows) : (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div
          className={stickyHeader ? 'overflow-auto' : 'overflow-x-auto'}
          style={stickyHeader ? { maxHeight: maxBodyHeight ?? 'calc(100vh - 240px)' } : undefined}
        >
          <table className="min-w-full divide-y divide-slate-200">
            <thead className={`bg-slate-50 ${stickyHeader ? 'sticky top-0 z-10 shadow-sm' : ''}`}>
              {visibleColumns.some((c) => c.headerGroup) && (
                <tr>
                  {selectable && <th scope="col" className="bg-slate-50" />}
                  {gruposCabecera(visibleColumns).map((g, i) => (
                    <th
                      key={`${g.label ?? ''}-${i}`}
                      scope="colgroup"
                      colSpan={g.span}
                      className={`px-4 pt-2 pb-1 text-center text-[11px] font-semibold uppercase tracking-wide ${
                        g.label ? `border-x border-b ${COLORES_GRUPO[coloresGrupo.get(g.label) ?? 'slate'].titulo}` : 'bg-slate-50'
                      }`}
                    >
                      {g.label}
                    </th>
                  ))}
                </tr>
              )}
              <tr>
                {selectable && (
                  <th scope="col" className="bg-slate-50 px-3 py-2.5" style={{ width: '2.5rem' }}>
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todo"
                      checked={
                        filteredRows.length > 0 &&
                        filteredRows.every((r) => selectedIds?.has(rowKey(r)))
                      }
                      ref={(el) => {
                        if (!el) return;
                        const visible = filteredRows.map((r) => rowKey(r));
                        const seleccionados = visible.filter((id) => selectedIds?.has(id)).length;
                        el.indeterminate = seleccionados > 0 && seleccionados < visible.length;
                      }}
                      onChange={(e) => {
                        if (!onSelectionChange) return;
                        const next = new Set(selectedIds ?? []);
                        const visible = filteredRows.map((r) => rowKey(r));
                        if (e.target.checked) {
                          for (const id of visible) next.add(id);
                        } else {
                          for (const id of visible) next.delete(id);
                        }
                        onSelectionChange(next);
                      }}
                      className="cursor-pointer"
                    />
                  </th>
                )}
                {visibleColumns.map((c) => {
                  const active = sortKey === c.key;
                  const sortable = c.sortable !== false;
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      onClick={sortable ? () => toggleSort(c.key) : undefined}
                      className={`${c.headerGroup ? COLORES_GRUPO[coloresGrupo.get(c.headerGroup) ?? 'slate'].columna : 'bg-slate-50 hover:bg-slate-100'} px-4 py-2.5 text-${c.align ?? 'left'} text-xs font-semibold uppercase tracking-wide text-slate-600 ${
                        sortable ? 'cursor-pointer select-none' : ''
                      } ${c.className ?? ''}`}
                      style={c.width ? { width: c.width } : undefined}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {sortable && (
                          <span className={`text-slate-400 ${active ? 'text-slate-700' : 'opacity-40'}`}>
                            {active ? (sortDesc ? '↓' : '↑') : '↕'}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading && (
                <tr>
                  <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-4 py-8">
                    <Spinner />
                  </td>
                </tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + (selectable ? 1 : 0)} className="px-4 py-8">
                    {empty ?? <EmptyState title="Sin resultados" description="Prueba a ajustar los filtros." />}
                  </td>
                </tr>
              )}
              {!loading &&
                (columnaGrupo
                  ? agrupar(filteredRows, columnaGrupo).flatMap((g) => [{ tipo: 'grupo' as const, g }, ...g.rows.map((row) => ({ tipo: 'fila' as const, row }))])
                  : filteredRows.map((row) => ({ tipo: 'fila' as const, row }))
                ).map((item) => {
                  if (item.tipo === 'grupo' && columnaGrupo) {
                    const g = item.g;
                    return (
                      <tr key={`grupo-${g.clave}`} className="bg-slate-100/80">
                        {selectable && <td className="px-3 py-1.5" />}
                        {visibleColumns.map((c, i) => (
                          <td key={c.key} className={`whitespace-nowrap px-4 py-1.5 text-xs font-semibold text-${c.align ?? 'left'} text-slate-700 ${c.className ?? ''}`}>
                            {i === 0
                              ? <span>{columnaGrupo.label}: <span className="text-slate-900">{g.etiqueta}</span> <span className="font-normal text-slate-500">· {g.rows.length}</span></span>
                              : c.total ? <span title="Subtotal del grupo">{c.total === 'avg' && <span className="mr-1 font-normal text-slate-400">ø</span>}{totalDeColumna(c, g.rows)}</span> : null}
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  if (item.tipo === 'grupo') return null;
                  const row = item.row;
                  const id = rowKey(row);
                  const selected = selectedIds?.has(id) ?? false;
                  const expanded = !!renderExpanded && (expandedIds?.has(id) ?? false);
                  return (
                    <Fragment key={id}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row) : undefined}
                      className={`${onRowClick ? 'cursor-pointer hover:bg-slate-50' : ''} ${selected ? 'bg-accent-50' : ''}`}
                    >
                      {selectable && (
                        <td
                          className="px-3 py-2.5"
                          style={{ width: '2.5rem' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            aria-label="Seleccionar fila"
                            checked={selected}
                            onChange={(e) => {
                              if (!onSelectionChange) return;
                              const next = new Set(selectedIds ?? []);
                              if (e.target.checked) next.add(id);
                              else next.delete(id);
                              onSelectionChange(next);
                            }}
                            className="cursor-pointer"
                          />
                        </td>
                      )}
                      {visibleColumns.map((c) => (
                        <td
                          key={c.key}
                          className={`whitespace-nowrap px-4 py-2.5 text-sm text-${c.align ?? 'left'} text-slate-700 ${c.className ?? ''}`}
                        >
                          {c.render ? c.render(row) : (row[c.key] as ReactNode) ?? '—'}
                        </td>
                      ))}
                    </tr>
                    {expanded && (() => {
                      const total = visibleColumns.length + (selectable ? 1 : 0);
                      const idx = expandedAlign ? visibleColumns.findIndex((c) => c.key === expandedAlign.key) : -1;
                      const antes = idx > 0 ? idx + (selectable ? 1 : 0) : 0;
                      return (
                        <tr className="bg-slate-50/70">
                          {antes > 0 && <td colSpan={antes} />}
                          <td colSpan={total - antes} className="px-4 py-2">
                            {/* width 0 + min-width 100 %: lo desplegado no ensancha las
                                columnas de la tabla al abrirse; si no cabe, desborda. */}
                            <div style={idx >= 0 ? { paddingLeft: expandedAlign?.indent, width: 0, minWidth: '100%' } : undefined}>
                              {renderExpanded(row)}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                    </Fragment>
                  );
                })}
            </tbody>
            {!loading && filteredRows.length > 0 && visibleColumns.some((c) => c.total) && (
              <tfoot className={`border-t border-slate-200 bg-slate-50 ${stickyHeader ? 'sticky bottom-0 z-10' : ''}`}>
                <tr>
                  {selectable && <td className="px-3 py-2" />}
                  {visibleColumns.map((c, i) => {
                    const primeraSinTotal = !c.total && visibleColumns.findIndex((x) => !x.total) === i;
                    return (
                      <td
                        key={c.key}
                        className={`whitespace-nowrap px-4 py-2 text-xs font-semibold text-${c.align ?? 'left'} text-slate-700 ${c.className ?? ''}`}
                        title={c.total ? (c.total === 'avg' ? 'Media de las filas visibles' : 'Total de las filas visibles') : undefined}
                      >
                        {c.total
                          ? <>{c.total === 'avg' && <span className="mr-1 font-normal text-slate-400">ø</span>}{totalDeColumna(c, filteredRows)}</>
                          : primeraSinTotal ? <span className="text-slate-500">Σ {filteredRows.length} fila{filteredRows.length === 1 ? '' : 's'}</span> : null}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      )}
      </div>
      {aside && (
        <div className="flex flex-col xl:sticky xl:top-0 xl:self-start" style={{ maxHeight: maxBodyHeight ?? 'calc(100vh - 240px)' }}>
          {aside}
        </div>
      )}
      </div>
    </div>
  );
}
